import api, { methods, assetsUrl } from "@api";
import Spacer from "@components/Spacer";
import Button from "@components/button";
import { useDownloadFile } from "@hooks/use-download-file";
import RadioButtonGroup from "@components/radio-button-group";
import { openBrowser } from "@features/auth/browser";
import Error from "@features/error/error";
import { useIssuersList } from "@provider/app-data";
import { useAuth } from "@provider/context-auth";
import { toast } from "@provider/toast";
import { useMutation } from "@tanstack/react-query";
import type { Account } from "@type/account";
import { paymentTypes } from "@utils";
import { Text, View } from "dripsy";
import * as React from "react";
import { useFormState } from "react-native-use-form";
import { bankIcons } from "@assets/banks";

export default function AccountChangePayment({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const { download, isLoading: downloadLoading, statusLabel } = useDownloadFile();
  const { data: issuersList, isError: isIssuersError, refetch: refetchIssuers } = useIssuersList();
  const { profile, refetch } = useAuth();

  const isSepa = profile?.payment_type! <= 2;
  const noMandate = isSepa && !profile?.pay_with_sepa;

  const [form, inputs] = useFormState(
    {
      paymentType: profile?.payment_type!,
      mandateBank: profile?.mandate_bank
        ? profile?.mandate_bank.DebtorBankId
        : (issuersList?.Nederland[0]?.DebtorBankId ?? ""),
    },
    {
      onSubmit: ({ mandateBank }) => {
        update({ data: issuersList?.Nederland.find((b) => b.DebtorBankId === mandateBank) });
      },
    }
  );

  const { mutate: getMandateRequestUrl, isPending: isMandateLoading } = useMutation({
    mutationKey: ["mandate-request"],
    mutationFn: (token: string) =>
      api<{ url: string; error: any }>({
        path: "/mandate-request",
        method: methods.GET,
      }),
    onError: (err) => {
      console.log("err", err);
      toast.show("Er is iets fout gegaan.");
    },
  });

  const { mutate: update, isPending: isLoading } = useMutation({
    mutationKey: ["update-bankdeptor"],
    mutationFn: (body: { data: any }) =>
      api<Account>({
        path: "/account/update-bankdebtor",
        body,
        method: methods.POST,
      }),
    onError: () => {
      toast.show("Er is iets fout gegaan.");
    },
    onSuccess: async (res) => {
      return getMandateRequestUrl(res.api_token, {
        onError: (err) => {
          console.log("err", err);
          toast.show("Er is iets fout gegaan.");
        },
        onSuccess: async (suc) => {
          await openBrowser(suc.url, "de-grote-verleiding://"); // Handles the native thing with .native import
          refetch();
          onClose();
          onSuccess();
        },
      });
    },
  });

  const canMandateOnline = noMandate && form.values.paymentType === 1;

  return (
    <>
      <RadioButtonGroup
        {...inputs.raw("paymentType", {
          required: true,
        })}
        values={paymentTypes.map((label, i) => {
          return { label, id: i + 1 };
        })}
      />

      {noMandate ? (
        <Error paddingTop="$1" paddingBottom="$3" error="(Nog geen machtiging gegeven)" isError={noMandate} />
      ) : (
        <Text sx={{ paddingTop: 6, paddingBottom: "$3" }}>
          {isSepa
            ? `Je hebt ons via een digitale SEPA- machtiging toestemming gegeven om de facturen door middel van automatische incasso (domiciliëring) te innen.`
            : `Je betaald door middel van iDeal. De Grote Verleiding biedt je ook de mogelijkheid om gemakkelijk te betalen door middel van automatische incasso (domiciliëring). Klik hieronder op 'Betaalgegevens wijzigen' om de betaalwijze aan te passen.`}
        </Text>
      )}
      {canMandateOnline ? (
        <>
          <Text sx={{ fontSize: 16, fontWeight: "bold", color: "$text", mb: "$2" }}>Selecteer bank</Text>
          {isIssuersError && (
            <Button outline onPress={() => refetchIssuers()}>
              Banken ophalen mislukt, probeer opnieuw
            </Button>
          )}
          {!issuersList?.Nederland?.length && !isIssuersError && (
            <Text sx={{ fontSize: 14, color: "$textSecondary", py: "$2" }}>Geen banken beschikbaar</Text>
          )}
          <RadioButtonGroup
            values={
              issuersList?.Nederland.map((bank) => ({
                id: bank.DebtorBankId,
                label: bank.DebtorBankName,
                icon: bankIcons[bank.DebtorBankId],
              })) ?? []
            }
            {...inputs.raw("mandateBank", {
              required: true,
            })}
          />
        </>
      ) : null}

      {form.values.paymentType === 2 ? (
        <View sx={{ mb: "$1" }}>
          <Button
            onPress={() => {
              download({
                url: assetsUrl + "/files/Machtiging_Domiciliering.pdf",
                fileName: "Machtiging_Domiciliering.pdf",
                withToken: false,
              });
            }}
            outline
            isLoading={downloadLoading}
            description={statusLabel}
          >
            Download machtigingsformulier
          </Button>
        </View>
      ) : null}
      <Spacer size="$4" />
      {canMandateOnline ? (
        <Button color="$primary" isLoading={isLoading || isMandateLoading} onPress={form.submit}>
          Geef machtiging
        </Button>
      ) : null}
    </>
  );
}
